/* The widget's default look, on somebody else's website.

   EVERY SELECTOR HERE IS ONE CLASS (0,1,0), deliberately. A customer overriding
   .mk-signup__input writes one class too, and styles.js inserts this sheet as
   the FIRST child of <head>, so their stylesheet comes later in source order and
   wins the tie. That is the whole override story: no !important, no id, no
   @layer. Appending to <head> instead would mean we win, which is the opposite
   of what this is for.

   Not @layer, though it is the textbook answer: a browser without it
   (Safari <= 15.3) drops the entire block including `font: inherit`, leaving
   UA-styled 13px Arial inputs inside a 17px serif page. That is inconsistent
   with a safari12 JavaScript target.

   Three state rules sit at two classes (:focus-visible, [disabled],
   [aria-invalid]). A focus ring is worth the extra specificity, because plenty
   of themes do `outline: none` on inputs.

   Nothing here sets a font-family, a text colour or a background colour on the
   container. The point is to look like the page it was pasted into. */

/* width: 100%, not just display: block -- some hosts (Square Online's own
   "Embed Code" widget among them) put this container in a flex layout with
   align-items: center rather than the default stretch. A flex item's
   width: auto means "size to content," not "fill the parent" the way a
   plain block's does, so without this the container shrinks to its widest
   unwrapped line (the SMS disclosure paragraph) and leaves the rest of the
   host's box blank. */
.mk-signup {
  display: block;
  width: 100%;
}

.mk-signup__heading {
  margin: 0 0 0.25em;
  font-size: 1.25em;
  font-weight: 600;
}

.mk-signup__intro {
  margin: 0 0 1em;
  opacity: 0.8;
}

.mk-signup__field {
  display: block;
  margin: 0 0 0.75em;
}

.mk-signup__label {
  display: block;
  margin: 0 0 0.25em;
}

/* `font: inherit`, not `font-family: inherit`, and this is the line the whole
   "looks native" claim rests on. Form controls do not inherit typography --
   the UA stylesheet sets `font` and `color` on them explicitly -- and `font` is
   the shorthand carrying family, size, weight, style AND line-height across in
   one declaration. `color` needs its own line because the UA sheet sets it to
   fieldtext. letter-spacing is not part of `font` and is not set by the UA
   sheet, so it already inherits. */
.mk-signup__input {
  font: inherit;
  color: inherit;
  box-sizing: border-box;
  display: block;
  width: 100%;
  padding: 0.55em 0.7em;
  background: transparent;
  /* Half-alpha mid grey, so it reads on a light page and a dark one. Any fixed
     grey is invisible on one of them, and we cannot know which we landed on. */
  border: 1px solid rgba(128, 128, 128, 0.55);
  border-radius: 0.25em;
}

/* iOS Safari zooms the viewport when a focused field computes below 16px, and
   plenty of host body text is 14px. max() keeps the inheritance -- 1em IS the
   inherited size -- and only puts a floor under it. */
@media (max-width: 640px) {
  .mk-signup__input {
    font-size: max(1em, 16px);
  }
}

.mk-signup__hint {
  display: block;
  margin: 0.25em 0 0;
  font-size: 0.85em;
  opacity: 0.75;
}

.mk-signup__consent {
  display: flex;
  gap: 0.5em;
  align-items: flex-start;
  margin: 0 0 0.75em;
}

.mk-signup__checkbox {
  font: inherit;
  flex: none;
  margin: 0.2em 0 0;
}

.mk-signup__consent-label {
  font-size: 0.9em;
}

.mk-signup__disclosure {
  margin: 0.75em 0 0;
  font-size: 0.8em;
  opacity: 0.75;
}

.mk-signup__disclosure-line {
  margin: 0 0 0.4em;
}

/* The accent is the BRAND's, set as a custom property on the container by
   widget.js from the config. currentColor is the fallback for a brand with no
   accent configured -- never a MessageKit colour on somebody else's site. */
.mk-signup__button {
  font: inherit;
  box-sizing: border-box;
  display: block;
  width: 100%;
  padding: 0.6em 1em;
  cursor: pointer;
  border: 1px solid transparent;
  border-radius: 0.25em;
  background: var(--mk-accent, currentColor);
  color: var(--mk-accent-text, #ffffff);
}

/* The alert region is in the DOM from the first paint and empty until it is
   not -- a live region created at the moment of the error is not announced by
   most screen readers. So it carries no margin of its own; the margin arrives
   with the modifier. */
.mk-signup__alert {
  display: block;
  margin: 0;
  font-size: 0.9em;
}

.mk-signup__alert--shown {
  margin: 0 0 0.75em;
}

.mk-signup__error {
  display: block;
  margin: 0.25em 0 0;
  font-size: 0.85em;
}

.mk-signup__skeleton {
  display: block;
  background: rgba(128, 128, 128, 0.15);
  border-radius: 0.25em;
}

.mk-signup__button[disabled] {
  opacity: 0.6;
  cursor: default;
}

.mk-signup__input:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: 2px;
}

.mk-signup__input[aria-invalid="true"] {
  border-color: rgba(190, 60, 60, 0.9);
}

/* The segment tick boxes. Every selector one class, like the rest of this
   sheet, so a customer's own one-class rule beats it on source order. */
.mk-signup__segments { display: block; margin: 0 0 0.75em; }
.mk-signup__segments-label { display: block; margin: 0 0 0.25em; }
.mk-signup__segment { display: flex; gap: 0.5em; align-items: flex-start; margin: 0 0 0.35em; }
.mk-signup__segment-label { font-size: 0.9em; }
